import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { DateAdapter, ErrorStateMatcher } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { LoginService } from '@ebw-monorepo/login';
import { FeatureSelectService } from '@ebw-monorepo/map';
import { safeJsonClone, safeJsonStringify } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalService } from '@ebw-monorepo/modal';
import { GeoJSON } from 'ol/format';
import { ExportService } from '../../../../services/export.service';
import { PsaService } from '../../../../services/psa.service';
import {
  formatChecklistDate,
  isChecklistCoordinatesField,
  isChecklistDateField,
  isChecklistNumberAllowed,
  isChecklistTextAllowed
} from '../../../../utils/checklist.utils';
import { Actions } from '../psa-configurator.model';

interface CheckListField {
  id: string;
  type?: string;
  field_name: string;
  field_type: string;
  section: string;
  group: string;
  description_title?: string;
  field_value?: any;
  default?: any;
  editable?: boolean;
  field_options?: string[];
  // field_type 'parametri': modalita' (collegato al TLC, ...) da indicare per ogni parametro
  field_modalita?: string[];
  // field_type 'dettagli': campi (%, km, ...) da compilare per ogni opzione
  field_campi?: string[];
  // field_type 'dettagli': 'text' for free text sub-fields (e.g. "Riferimento nel cloud"), numeric otherwise
  field_campi_type?: 'number' | 'text';
  // template detail request shown above the notes field, where the user answers it (e.g. "Specificare se ...")
  etichetta_note?: string | null;
  // template legend or reference shown under the item title (e.g. "Se sì, compilare la scheda ...")
  indicazione?: string | null;
  // field_type 'sottolivelli': domande che si aprono sotto un'opzione (es. "Zona" sotto il "sì")
  field_sottolivelli?: Record<string, Sottolivello[]>;
  note: string;
}

interface Sottolivello {
  titolo: string;
  tipo: 'radio' | 'check';
  obbligatorio: boolean;
  opzioni: string[];
  // testo mostrato al posto della sigla (es. "ZTA (zona di tutela assoluta)"); la risposta salvata resta la sigla
  etichette?: Record<string, string>;
}

// field_type 'parametri':    field_options = {opzioni, modalita},    risposta = {parametro: modalita}
// field_type 'dettagli':     field_options = {opzioni, campi},       risposta = {opzione: {campo: valore}}
// field_type 'sottolivelli': field_options = {opzioni, sottolivelli}, risposta = {risposta, sottolivelli: {titolo: valore}}
const PARAMETRO_NESSUNO = 'nessuno';

interface GroupList {
  title: string;
  sections: {
    title: string;
  }[];
  expanded: boolean;
}

interface CheckListComponentParams {
  entity: any;
}

/**
 * Component responsible for displaying and managing a checklist within a dialog.
 * The checklist is loaded from the server and displayed in sections. Users can fill out
 * the checklist and submit their responses.
 *
 * @class CheckListComponent
 *
 * @author Tommaso Lazzaretti
 *
 */
@Component({
  selector: 'ebw-monorepo-check-list',
  templateUrl: './check-list.component.html',
  styleUrls: ['./check-list.component.scss']
})
export class CheckListComponent implements OnInit {
  @ViewChild(MatAccordion) accordion: MatAccordion;

  /**
   * The list of checklist fields, each representing a form field in a section.
   * @type {CheckListField[]}
   */
  public checkListSection: CheckListField[] = null;
  public groupList: GroupList[] = null;
  // nome della checklist ministeriale prevista per la tipologia dell'elemento
  public checklistTitle: string = null;
  public readonly parametroNessuno = PARAMETRO_NESSUNO;
  private userId: string;
  public PSAentity: any;

  /**
   * Constructor to initialize the component.
   * Injects necessary services and initializes the checklist data.
   *
   * @param entity_id - The entity ID for which the checklist is being displayed or filled out.
   * @param _psaService - Service to handle operations related to PSA.
   * @param _loginService - Service to manage user-related data.
   * @param _dialogRef - Reference to the current dialog, used to close the dialog on completion.
   * @param _featureSelectService
   * @param _exportService
   */
  constructor(
    @Inject(MAT_DIALOG_DATA) public entity_id: string, // string id
    private _psaService: PsaService,
    private _loginService: LoginService,
    private _dialogRef: MatDialogRef<CheckListComponent>,
    private _featureSelectService: FeatureSelectService,
    private _exportService: ExportService,
    private _modalService: EbwModalService,
    private _dateAdapter: DateAdapter<unknown>
  ) {}

  ngOnInit() {
    this._psaService._getDetail(this.entity_id).subscribe((resp) => {
      this.PSAentity = resp;

      const feature = new GeoJSON().readFeature(
        {
          id: resp.urn,
          type: 'Feature',
          geometry: safeJsonClone(resp.geom),
          properties: {
            ...resp
          }
        },
        {}
      );

      this._featureSelectService.featuresSelected = [feature];
      this.getChecklist();
    });

    this.userId = this._loginService.getUserId();
  }

  // -----------------------------------------------------------------------------------------------------------------------

  getChecklist() {
    this._psaService._getCheckList(this.entity_id).subscribe((items) => {
      items.filter((item) => this.hasOpzioniConDettaglio(item)).forEach((item) => this.parseOpzioniConDettaglio(item));
      this.checkListSection = items;
      this.checklistTitle = items[0]?.type ?? null;

      // Gruppi e sezioni nell'ordine in cui arrivano le voci (ordine del template ministeriale)
      const groupMap = new Map<string, Set<string>>();
      items.forEach((item) => {
        if (!groupMap.has(item.group)) {
          groupMap.set(item.group, new Set());
        }
        groupMap.get(item.group).add(item.section);
      });

      this.groupList = Array.from(groupMap.entries()).map(([group, sections]) => ({
        title: group,
        sections: Array.from(sections).map((section) => ({ title: section })),
        expanded: false
      }));

      this._psaService._getCheckListAnswers(this.entity_id).subscribe({
        next: (resp) => {
          this.updateChecklistWithResponses(resp);
        },
        error: (err) => {
          console.error('Error retrieving checlist answer', err);
        }
      });
    });
  }

  // -----------------------------------------------------------------------------------------------------------------------

  checklistItemDividedBySectionAndGroup(section: string, group: string) {
    return this.checkListSection.filter((c) => {
      const isSectionItem = c.section === section;
      const isGroupItem = c.group === group;
      return isSectionItem && isGroupItem;
    });
  }

  // -----------------------------------------------------------------------------------------------------------------------

  /**
   * Submits the checklist answers by calling the service API.
   * Filters out fields that are not filled and sends the remaining ones.
   */
  onConfirm() {
    // scelte multiple e parametri si salvano come JSON, cosi le opzioni con la virgola restano intere;
    // le voci con la sola nota si salvano senza risposta, altrimenti la nota andrebbe persa
    const dataArray = this.checkListSection
      .filter((item) => !this.isFieldEmpty(item) || !!item.note?.trim())
      .map((item) => {
        if (this.isFieldEmpty(item)) {
          return { ...item, field_value: null };
        }
        return item.field_value !== null && typeof item.field_value === 'object'
          ? { ...item, field_value: safeJsonStringify(item.field_value) ?? '' }
          : item;
      });

    this._psaService
      ._upsertCheckListAnswers({
        entity_id: this.entity_id,
        user_id: this.userId,
        dataArray
      })
      .subscribe({
        next: () => {
          const message = 'Salvataggio avvenuto con successo';
          this._modalService.openInfoModal({ title: 'Checklist', content: message });
          this._psaService.checklistChanged$.next();
          this.getChecklist();
        },
        error: (error) => {
          this._modalService.openInfoModal({ title: 'Checklist', content: 'Errore durante il salvataggio' });
          console.error(error);
        }
      });
  }

  /**
   * Closes the dialog without saving any data.
   */
  onCancel() {
    this._dialogRef.close();
  }

  /**
   * Closes the dialog and opens the panel to copy this checklist to other elements.
   * Unsaved changes are discarded, as with cancel.
   */
  duplicate() {
    this._dialogRef.close();
    this._psaService.openDuplicatePsa(this.PSAentity, 'CHECKLIST');
  }

  /**
   * Handles the change event for checkbox fields, updating the checklist item accordingly.
   *
   * @param {any} event - The checkbox change event.
   * @param {string} option - The option being toggled.
   * @param {CheckListField} field - The checklist field containing the checkbox options.
   */
  onCheckboxChange(event: any, option: string, field: any) {
    // Se field_value non esiste o non è un array, inizializzalo come un array vuoto
    if (!Array.isArray(field.field_value)) {
      field.field_value = [];
    }

    // Se la checkbox è selezionata, aggiungi il valore all'array
    if (event.checked) {
      field.field_value.push(option);
    } else {
      // Se la checkbox è deselezionata, rimuovi il valore dall'array
      const index = field.field_value.indexOf(option);
      if (index > -1) {
        field.field_value.splice(index, 1);
      }
    }
  }

  /**
   * Checks if a specific checkbox option is selected for a field.
   *
   * @param {string} option - The checkbox option.
   * @param {CheckListField} field - The checklist field.
   * @returns {boolean} - True if the option is selected, otherwise false.
   */
  isChecked(option: string, field: any): boolean {
    return Array.isArray(field.field_value) && field.field_value.includes(option);
  }

  hasOpzioniConDettaglio(field: CheckListField): boolean {
    return ['parametri', 'dettagli', 'sottolivelli'].includes(field.field_type);
  }

  sottolivelliDellaRisposta(field: CheckListField): Sottolivello[] {
    return field.field_sottolivelli?.[field.field_value?.risposta] ?? [];
  }

  // cambiando risposta si azzerano i sottolivelli della precedente
  onRispostaChange(opzione: string, field: CheckListField) {
    field.field_value = { risposta: opzione, sottolivelli: {} };
  }

  isSottolivelloChecked(field: CheckListField, sottolivello: Sottolivello, opzione: string): boolean {
    const valore = field.field_value?.sottolivelli?.[sottolivello.titolo];
    return Array.isArray(valore) && valore.includes(opzione);
  }

  onSottolivelloCheckChange(event: any, field: CheckListField, sottolivello: Sottolivello, opzione: string) {
    const valori: string[] = (field.field_value.sottolivelli[sottolivello.titolo] ?? []).filter((v: string) => v !== opzione);
    field.field_value.sottolivelli[sottolivello.titolo] = event.checked ? [...valori, opzione] : valori;
  }

  isOpzioneSelezionata(field: CheckListField, opzione: string): boolean {
    return !!field.field_value && Object.prototype.hasOwnProperty.call(field.field_value, opzione);
  }

  onDettaglioChange(event: any, opzione: string, field: CheckListField) {
    if (!field.field_value || typeof field.field_value !== 'object') {
      field.field_value = {};
    }

    if (event.checked) {
      field.field_value[opzione] = {};
    } else {
      delete field.field_value[opzione];
    }

    if (Object.keys(field.field_value).length === 0) {
      field.field_value = null;
    }
  }

  /**
   * "nessuno" esclude gli altri parametri; un parametro appena selezionato non ha ancora la modalita'.
   */
  onParametroChange(event: any, parametro: string, field: CheckListField) {
    if (!field.field_value || typeof field.field_value !== 'object') {
      field.field_value = {};
    }

    if (event.checked) {
      if (parametro === PARAMETRO_NESSUNO) {
        field.field_value = {};
      } else {
        delete field.field_value[PARAMETRO_NESSUNO];
      }
      field.field_value[parametro] = null;
    } else {
      delete field.field_value[parametro];
    }

    if (Object.keys(field.field_value).length === 0) {
      field.field_value = null;
    }
  }

  private parseOpzioniConDettaglio(item: CheckListField) {
    try {
      const parsed = JSON.parse(item.field_options as unknown as string);
      item.field_options = parsed.opzioni ?? [];
      item.field_modalita = parsed.modalita ?? [];
      item.field_campi = parsed.campi ?? [];
      item.field_campi_type = parsed.tipo_campi === 'text' ? 'text' : 'number';
      item.field_sottolivelli = parsed.sottolivelli ?? {};
    } catch {
      item.field_options = [];
      item.field_modalita = [];
      item.field_campi = [];
      item.field_sottolivelli = {};
    }
  }

  private parseOpzioniConDettaglioValue(value: string): Record<string, any> | null {
    try {
      const parsed = JSON.parse(value);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || Object.keys(parsed).length === 0) {
        return null;
      }
      if ('risposta' in parsed && !parsed.sottolivelli) {
        parsed.sottolivelli = {};
      }
      return parsed;
    } catch {
      return null;
    }
  }

  /**
   * Updates the checklist fields with the responses fetched from the API.
   *
   * @param {any[]} responses - The list of responses from the API, mapped to checklist items.
   */
  updateChecklistWithResponses(responses: any[]) {
    // Itera sulla tua lista checkListSection
    this.checkListSection.forEach((sectionItem: CheckListField) => {
      // Trova la risposta corrispondente basata sul checklist_id
      const matchingResponse = responses.find((response) => response.checklist_id === sectionItem.id);

      // Se c'è una risposta corrispondente, aggiorna il field_value
      if (matchingResponse) {
        if (sectionItem.field_type === 'check' && typeof matchingResponse.field_value === 'string') {
          sectionItem.field_value = this.parseMultipleChoice(matchingResponse.field_value);
        } else if (this.hasOpzioniConDettaglio(sectionItem) && typeof matchingResponse.field_value === 'string') {
          sectionItem.field_value = this.parseOpzioniConDettaglioValue(matchingResponse.field_value);
        } else {
          // Gestione per i campi non array
          sectionItem.field_value = matchingResponse.field_value;
        }
        sectionItem.note = matchingResponse.note;
      }
    });
  }

  /**
   * Una voce e' compilata se ha un valore non vuoto; una scelta multipla senza opzioni selezionate non conta.
   */
  /**
   * The ministerial "Note" and "Ulteriori note finali" rows: their text field is the note,
   * so the generic notes field is not shown
   */
  isNotesItem(field: CheckListField): boolean {
    const name = (field.field_name ?? '').trim().toLowerCase();
    return field.field_type === 'text' && (name === 'note' || name === 'ulteriori note finali');
  }

  isNumberAllowed(name: string, value: unknown): boolean {
    return isChecklistNumberAllowed(name, value);
  }

  private readonly _numberMatchers = new Map<string, ErrorStateMatcher>();

  /**
   * Shows a numeric field in error with the same rule that disables Save. One matcher per field name,
   * so change detection always receives the same instance.
   */
  numberMatcher(name: string): ErrorStateMatcher {
    if (!this._numberMatchers.has(name)) {
      this._numberMatchers.set(name, { isErrorState: (control) => !!control && !isChecklistNumberAllowed(name, control.value) });
    }
    return this._numberMatchers.get(name);
  }

  /** Translation key of the rule shown under a numeric field with a value out of range */
  numberRuleKey(name: string): string {
    return (name ?? '').trim().toLowerCase().startsWith('numero')
      ? 'map.sidebar.psa.check_list.number_rule_integer'
      : 'map.sidebar.psa.check_list.number_rule_not_negative';
  }

  readonly today = new Date();
  private readonly _textMatchers = new Map<string, ErrorStateMatcher>();

  isDateField(name: string): boolean {
    return isChecklistDateField(name);
  }

  isCoordinatesField(name: string): boolean {
    return isChecklistCoordinatesField(name);
  }

  textPlaceholder(name: string): string {
    if (isChecklistDateField(name)) return 'gg/mm/aaaa';
    if (isChecklistCoordinatesField(name)) return '45.6983, 9.6773';
    return '';
  }

  /** Shows a date or coordinates field in error with the same rule that disables Save */
  textMatcher(name: string): ErrorStateMatcher {
    if (!this._textMatchers.has(name)) {
      this._textMatchers.set(name, { isErrorState: (control) => !!control && !isChecklistTextAllowed(name, control.value) });
    }
    return this._textMatchers.get(name);
  }

  /** The calendar returns the date in the format of the configured date adapter (native Date or Moment) */
  onDatePicked(field: CheckListField, date: unknown): void {
    if (!date) return;
    const adapter = this._dateAdapter;
    field.field_value = formatChecklistDate(adapter.getDate(date), adapter.getMonth(date) + 1, adapter.getYear(date));
  }

  /**
   * Save is disabled while a value is not allowed: negative number, decimal count, percentage over 100,
   * date not dd/mm/yyyy or in the future, coordinates without two numbers
   */
  get hasInvalidValues(): boolean {
    return (this.checkListSection ?? []).some((field) => {
      if (field.field_type === 'number') {
        return !this.isNumberAllowed(field.field_name, field.field_value);
      }
      if (field.field_type === 'text') {
        return !isChecklistTextAllowed(field.field_name, field.field_value);
      }
      if (field.field_type === 'dettagli' && field.field_campi_type !== 'text' && field.field_value && typeof field.field_value === 'object') {
        return Object.values(field.field_value as Record<string, Record<string, unknown>>).some((subFields) =>
          Object.entries(subFields ?? {}).some(([subField, value]) => !this.isNumberAllowed(subField, value))
        );
      }
      return false;
    });
  }

  isFieldEmpty(field: CheckListField): boolean {
    const value = field.field_value;
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (field.field_type === 'sottolivelli') {
      return !value?.risposta;
    }
    if (value !== null && typeof value === 'object') {
      return Object.keys(value).length === 0;
    }
    return value === null || value === undefined || value === '';
  }

  /**
   * Pallino rosso: voce vuota, parametri selezionati senza modalita', opzioni con campi non
   * compilati o sottolivelli obbligatori senza risposta.
   */
  isFieldDaCompletare(field: CheckListField): boolean {
    if (this.isFieldEmpty(field)) {
      return true;
    }
    if (field.field_type === 'sottolivelli') {
      return this.sottolivelliDellaRisposta(field).some((s) => {
        const valore = field.field_value.sottolivelli?.[s.titolo];
        return s.obbligatorio && (!valore || (Array.isArray(valore) && valore.length === 0));
      });
    }
    if (field.field_type === 'parametri') {
      return Object.entries(field.field_value).some(([parametro, modalita]) => parametro !== PARAMETRO_NESSUNO && !modalita);
    }
    // text sub-fields (document references) are optional: selecting the options is enough
    if (field.field_type === 'dettagli' && field.field_campi_type !== 'text') {
      return Object.values(field.field_value).some((valori: any) =>
        field.field_campi.some((campo) => valori?.[campo] === null || valori?.[campo] === undefined || valori?.[campo] === '')
      );
    }
    return false;
  }

  /**
   * Risposta in forma leggibile per l'export.
   */
  formatRisposta(item: CheckListField): string {
    const value = item.field_value;
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (item.field_type === 'sottolivelli' && value) {
      const sottolivelli = Object.entries(value.sottolivelli ?? {})
        .filter(([, v]) => (Array.isArray(v) ? v.length > 0 : !!v))
        .map(([titolo, v]) => `${titolo}: ${Array.isArray(v) ? v.join(', ') : v}`);
      return sottolivelli.length ? `${value.risposta} (${sottolivelli.join('; ')})` : value.risposta;
    }
    if (value !== null && typeof value === 'object') {
      return Object.entries(value)
        .map(([opzione, dettaglio]) => {
          if (dettaglio !== null && typeof dettaglio === 'object') {
            const campi = Object.entries(dettaglio)
              .filter(([, v]) => v !== null && v !== '')
              .map(([campo, v]) => `${campo}: ${v}`);
            return campi.length ? `${opzione} (${campi.join(', ')})` : opzione;
          }
          return dettaglio ? `${opzione}: ${dettaglio}` : opzione;
        })
        .join('; ');
    }
    return value?.toString()?.trim() || '';
  }

  /**
   * Legge una risposta a scelta multipla salvata come array JSON (["a","b"]) o, per i dati
   * precedenti, come array Postgres ({a,"b, c"}).
   */
  parseMultipleChoice(value: string): string[] {
    const trimmed = value.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed.map((v) => String(v)) : [];
      } catch {
        return [];
      }
    }
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      const matches = trimmed.slice(1, -1).match(/"((?:[^"\\]|\\.)*)"|[^,]+/g) ?? [];
      return matches.map((m) => (m.startsWith('"') ? m.slice(1, -1).replace(/\\(.)/g, '$1') : m.trim())).filter(Boolean);
    }
    return trimmed ? [trimmed] : [];
  }

  /**
   * Parses a JSON string to an array of strings.
   *
   * @param {string} input - The JSON string input.
   * @returns {string[]} - The parsed array of strings.
   */
  parseStringToArray(input: string): string[] {
    try {
      // Usa JSON.parse per convertire la stringa in un array
      const parsedArray = JSON.parse(input);

      // Verifica se il risultato è effettivamente un array
      if (Array.isArray(parsedArray)) {
        return parsedArray;
      } else {
        throw new Error('Il risultato non è un array');
      }
    } catch (error) {
      console.error('Errore durante il parsing della stringa:', error);
      return [];
    }
  }

  public async addDetailFromFeature(feature) {
    if (!feature) {
      return;
    }
    await this._featureSelectService.mapFeatureToModal(feature);
  }

  // ------------------------------------------------------------------------------------------------------------------------

  exportChecklist(type: 'pdf' | 'excel', downloadInFIlesystem: boolean) {
    const title = 'Checklist';
    if (!this.checkListSection || this.checkListSection.length === 0) {
      const content = "Nessun dato disponibile per l'export";
      this._modalService.openInfoModal({ title, content });
      return;
    }

    switch (type) {
      case 'pdf':
        this.exportChecklist_pdf(downloadInFIlesystem);
        break;

      case 'excel':
        this.exportChecklist_excel();
        break;

      default:
        this.exportChecklist_excel();
        break;
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------

  exportChecklist_excel(): void {
    const title = 'Checklist';
    // Creiamo la struttura dei dati per l'export
    const groupedData: Record<string, { gruppo: string; sezione: string; domanda: string; risposta: string; note: string }[]> = {};

    // Iteriamo sull'array di elementi
    this.checkListSection.forEach((item) => {
      const gruppo = item.group?.trim() || 'Senza Gruppo';
      const sezione = item.section?.trim() || '';
      const domanda = item.description_title?.trim() || '';
      const risposta = this.formatRisposta(item);
      const note = item.note?.trim() || '';

      if (domanda && risposta) {
        // Se il gruppo non esiste, inizializziamo un array vuoto
        if (!groupedData[gruppo]) {
          groupedData[gruppo] = [];
        }

        // Aggiungiamo l'oggetto alla lista del gruppo corrispondente
        groupedData[gruppo].push({ gruppo, sezione, domanda, risposta, note });
      }
    });

    if (Object.keys(groupedData).length === 0) {
      const content = "Nessun dato valido per l'export";
      this._modalService.openInfoModal({ title, content });
      return;
    }

    // Chiamata all'ExportService per generare il PDF
    const nomeFile = `Checklist_${this.PSAentity.search_term}_${this._psaService.getFormattedDate()}`;

    this._exportService.export('excel', groupedData, nomeFile);
  }

  /**
   * Export the checklist data to a PDF file.
   */
  exportChecklist_pdf(downloadInFilesystem: boolean): void {
    const title = 'Checklist';
    const groupedData = new Map<string, Map<string, { domanda: string; risposta: string; note: string }[]>>();

    this.checkListSection.forEach((item) => {
      const groupTitle = item.group?.trim() || 'Senza Gruppo';
      const sectionTitle = item.section?.trim() || '';
      const domanda = item.description_title?.trim();
      const risposta = this.formatRisposta(item);
      const note = item.note?.trim();

      if (domanda && risposta) {
        if (!groupedData.has(groupTitle)) {
          groupedData.set(groupTitle, new Map());
        }

        const sectionMap = groupedData.get(groupTitle)!;

        if (!sectionMap.has(sectionTitle)) {
          sectionMap.set(sectionTitle, []);
        }

        sectionMap.get(sectionTitle)?.push({ domanda, risposta, note });
      }
    });

    if (groupedData.size === 0) {
      const content = "Nessun dato valido per l'export";
      this._modalService.openInfoModal({ title, content });
      return;
    }

    // Creiamo il contenuto strutturato per il PDF
    const pdfContent: { text?: string; style?: string; table?: any; layout?: string }[] = [];

    groupedData.forEach((sections, groupTitle) => {
      pdfContent.push({ text: groupTitle, style: 'groupTitle' });

      sections.forEach((domandeRisposte, sectionTitle) => {
        // i gruppi senza sotto-sezione (tutti tranne TRATTAMENTO) non hanno titolo di sezione
        if (sectionTitle) {
          pdfContent.push({ text: sectionTitle, style: 'sectionTitle' });
        }
        pdfContent.push(
          {
            table: {
              widths: ['35%', '35%', '30%'],
              body: [...domandeRisposte.map((d) => [d.domanda, d.risposta, d.note])]
            },
            layout: 'lightHorizontalLines'
          },
          { text: '\n' } // Spazio tra le sezioni
        );
      });
    });

    const nomeFile = `Checklist_${this.PSAentity.search_term}_${this._psaService.getFormattedDate()}.pdf`;

    // Chiamata all'ExportService per generare il PDF
    if (downloadInFilesystem) {
      this._psaService
        ._downloadPdfChecklistInFilesystem({
          content: pdfContent,
          styles: this.getPdfStyles(),
          elementId: this.PSAentity.id,
          nomeFile
        })
        .subscribe(() => {
          this._modalService.openInfoModal({
            title: 'Checklist',
            content: nomeFile + ' aggiunta con successo'
          });
          return;
        });
      return;
    }

    this._exportService.export('pdf_checklist', { content: pdfContent, styles: this.getPdfStyles() }, nomeFile);
  }

  // Stili per migliorare la leggibilità nel PDF
  getPdfStyles() {
    return {
      groupTitle: {
        fontSize: 16,
        bold: true,
        margin: [0, 10, 0, 10],
        color: '#003366'
      },
      sectionTitle: {
        fontSize: 14,
        bold: true,
        margin: [0, 8, 0, 5],
        decoration: 'underline'
      },
      tableHeader: {
        bold: true,
        fillColor: '#959595'
      }
    };
  }

  protected readonly Actions = Actions;
}
